import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useGetListById from 'Hooks/query-hooks/list/useGetListById';
import { IMPORT_EXPORT_PROCESS_TYPE } from 'Common/enums';
import { RQ_ALL_LIST } from 'Utils';
import { getCustomList } from 'Hooks/query-hooks/helper';

type Props = {
    currentListId: number;
    processType: IMPORT_EXPORT_PROCESS_TYPE;
    mutation:
        | UseMutationResult<ExportAllListEntries, unknown, ExportAllListEntriesSchema, unknown>
        | UseMutationResult<ImportListEntries, unknown, ImportListEntriesSchema, unknown>;
};

type StartImportOrExportParams = {
    listId: number;
    listName: string;
    fileName?: string;
};

const useGetUpdatedSingleListAndStartImportOrExport = ({ currentListId, processType, mutation }: Props) => {
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const currentList = useGetListById(currentListId);
    const _formattedProcessType = {
        lowerCase: processType.toLowerCase(),
        capitalCase: processType.charAt(0).toUpperCase() + processType.slice(1).toLowerCase()
    };
    const queryClient = useQueryClient();
    const history = useHistory();
    const location = useLocation();
    const importMutation = mutation as UseMutationResult<ImportListEntries, unknown, ImportListEntriesSchema, unknown>;
    const exportMutation = mutation as UseMutationResult<
        ExportAllListEntries,
        unknown,
        ExportAllListEntriesSchema,
        unknown
    >;

    const callBack = React.useCallback(
        async ({ listId, listName, fileName }: StartImportOrExportParams) => {
            try {
                const { error, isRefetchError, isSuccess } = (await currentList.refetch()) as {
                    error: AxiosError;
                    isRefetchError: boolean;
                    isSuccess: boolean;
                };
                if (isRefetchError && error?.response?.status === 404) {
                    // Determines list-management page route.
                    const goToListManagementPage = location.pathname.split('/').slice(0, 2).join('/');
                    // go back to previous page if list doesn't exist & show alert.
                    history.push(goToListManagementPage);

                    addAlert({
                        id: `list-${_formattedProcessType.lowerCase}-error`,
                        status: 'danger',
                        content: t('List deleted by another user', {
                            processType: t(_formattedProcessType.capitalCase),
                            listName
                        })
                    });
                    await queryClient.invalidateQueries([RQ_ALL_LIST]);
                } else if (isSuccess && processType === IMPORT_EXPORT_PROCESS_TYPE.EXPORT) {
                    exportMutation.mutate({ listId, listName });
                } else if (isSuccess && processType === IMPORT_EXPORT_PROCESS_TYPE.IMPORT) {
                    if (fileName) {
                        const customListType = currentList.data?.customListType?.listType;
                        let payload;
                        if (customListType) {
                            const customList = getCustomList(customListType);
                            payload = {
                                customListTypeDetails: {
                                    listType: customList.customListType,
                                    validationDetails: customList.validationDetails.value
                                }
                            };
                        }
                        importMutation.mutate({
                            listId,
                            listName,
                            fileName,
                            ...(customListType && payload)
                        });
                    } else throw new Error('Import entries from CSV needs a valid fileName');
                }
            } catch {
                // generic error message for API errors based on processType.
                addAlert({
                    id: `start-${_formattedProcessType.lowerCase}-error`,
                    status: 'danger',
                    content: t(`Start List ${_formattedProcessType.capitalCase} Error`)
                });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [exportMutation, importMutation, processType]
    );
    return callBack;
};

export default useGetUpdatedSingleListAndStartImportOrExport;
